import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Activity, GripVertical, LocateFixed, ScanLine, SlidersHorizontal, X } from "lucide-react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { cn } from "@perceptron/utils/cn";

import type { TimelinePlayerBridge } from "../../bridge";
import { sameInspectorSettings, useTrackingInspectorStore } from "./controller";
import { InspectPanel } from "./InspectPanel";
import { RunPanel } from "./RunPanel";
import { StrategiesPanel, StrategyActions } from "./StrategiesPanel";
import { useInspectorPosition } from "./useInspectorPosition";

const copy = strings.trackingInspector;
const TABS = [
  { value: "inspect", label: copy.inspect, icon: ScanLine },
  { value: "run", label: copy.run, icon: Activity },
  { value: "strategies", label: copy.strategies, icon: SlidersHorizontal },
] as const;

/** A per-player inspector that can float clear of the video without blocking playback. */
export function TrackingInspector({ bridge }: { bridge: TimelinePlayerBridge }) {
  const enabled = useTrackingInspectorStore((state) => state.enabled);
  const open = useTrackingInspectorStore((state) => state.open);
  const tab = useTrackingInspectorStore((state) => state.tab);
  const summary = useTrackingInspectorStore((state) => state.summary?.summary);
  const busy = useTrackingInspectorStore((state) => state.summary?.busy);
  const setOpen = useTrackingInspectorStore((state) => state.setOpen);
  const setTab = useTrackingInspectorStore((state) => state.setTab);
  const setFilter = useTrackingInspectorStore((state) => state.setFilter);
  const [trackSearch, setTrackSearch] = useState("");
  const draftChanged = useTrackingInspectorStore(
    (state) => !sameInspectorSettings(state.activeSettings, state.draftSettings),
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const inspectRef = useRef<HTMLButtonElement>(null);
  const runRef = useRef<HTMLButtonElement>(null);
  const strategiesRef = useRef<HTMLButtonElement>(null);
  const id = useId();
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);
  const [portalContainer, setPortalContainer] = useState<Element | null>(null);
  useEffect(() => {
    // Move a stable host into fullscreen so React keeps the inspector subtree,
    // including search, scroll positions, and expanded details, mounted.
    const host = document.createElement("div");
    host.style.display = "contents";
    const update = () => {
      const container = document.fullscreenElement ?? document.body;
      if (host.parentElement !== container) {
        // Browsers reset scroll offsets when a subtree is detached/reinserted.
        const scrollPositions = Array.from(host.querySelectorAll<HTMLElement>("*"))
          .filter((element) => element.scrollTop || element.scrollLeft)
          .map((element) => ({ element, top: element.scrollTop, left: element.scrollLeft }));
        container.appendChild(host);
        for (const { element, top, left } of scrollPositions) {
          element.scrollTop = top;
          element.scrollLeft = left;
        }
      }
      setPortalContainer(container);
    };
    setPortalHost(host);
    update();
    document.addEventListener("fullscreenchange", update);
    return () => {
      document.removeEventListener("fullscreenchange", update);
      host.remove();
    };
  }, []);
  const position = useInspectorPosition({
    open: enabled && open && !!portalContainer,
    triggerRef,
    panelRef,
    container: portalContainer,
  });
  const wasOpen = useRef(false);
  useLayoutEffect(() => {
    if (open) (tab === "inspect" ? inspectRef : tab === "run" ? runRef : strategiesRef).current?.focus();
    else if (wasOpen.current) triggerRef.current?.focus();
    wasOpen.current = open;
  }, [open, tab, portalContainer]);
  const close = () => setOpen(false);
  const moveTab = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const index = TABS.findIndex((item) => item.value === tab);
    const next =
      event.key === "Home"
        ? "inspect"
        : event.key === "End"
          ? "strategies"
          : TABS[(index + (event.key === "ArrowRight" ? 1 : TABS.length - 1)) % TABS.length]!.value;
    setTab(next);
    (next === "inspect" ? inspectRef : next === "run" ? runRef : strategiesRef).current?.focus();
  };
  if (!enabled) return null;

  return (
    <div
      className="dark absolute right-4 top-2 z-40 max-w-[calc(100%-2rem)] font-sans text-sm normal-case leading-5 text-foreground"
      data-state={open ? "open" : "closed"}
      data-tracking-inspector
    >
      {/*
        The visible way in is the icon in the timeline's control tray. This
        anchors the panel's position and is where focus returns on close, so it
        stays in the tree — revealed on focus so a keyboard user can see where
        they have landed rather than reaching something invisible.
      */}
      <button
        aria-controls={open ? `${id}-inspector` : undefined}
        aria-expanded={open}
        aria-label={copy.title}
        className="sr-only right-0 top-0 focus-visible:not-sr-only focus-visible:absolute focus-visible:rounded-md focus-visible:bg-background focus-visible:px-2 focus-visible:py-1 focus-visible:ring-1 focus-visible:ring-ring"
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
      />
      {busy && !open && (
        <div
          className="pointer-events-none mt-2 flex h-10 max-w-full items-center gap-2 rounded-lg border border-border bg-background px-3"
          role="status"
        >
          <ScanLine aria-hidden="true" className="size-4 shrink-0 animate-pulse text-foreground-secondary" />
          <span className="min-w-0 truncate text-xs font-normal text-foreground-secondary" data-tracking-summary>
            {summary ?? copy.waitingForData}
          </span>
        </div>
      )}
      {open &&
        portalHost &&
        createPortal(
          <section
            aria-label={copy.title}
            aria-modal="false"
            className="dark z-50 flex h-[min(640px,calc(100dvh-24px))] w-[560px] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-xl border border-border bg-background font-sans text-sm normal-case leading-5 text-foreground [container-type:inline-size]"
            data-dragging={position.isDragging || undefined}
            data-tracking-inspector
            id={`${id}-inspector`}
            ref={panelRef}
            role="dialog"
            style={position.style}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              // The panel owns its keys, including header arrows and Home. Portals
              // still bubble through the player React tree and document shortcuts.
              event.stopPropagation();
              if (event.key === "Escape") {
                event.preventDefault();
                close();
              }
            }}
          >
            <div className="flex shrink-0 items-center gap-1 border-b border-border px-2 py-2">
              <div
                {...position.dragHandleProps}
                aria-describedby={`${id}-move-hint`}
                aria-label={copy.move}
                className={cn(
                  "flex min-w-0 flex-1 touch-none select-none items-center gap-2 rounded-md px-2 py-1 outline-none focus-visible:ring-1 focus-visible:ring-ring",
                  position.isDragging ? "cursor-grabbing" : "cursor-grab",
                )}
                role="button"
                tabIndex={0}
                title={copy.moveHint}
              >
                <GripVertical aria-hidden="true" className="size-4 shrink-0 text-foreground-secondary" />
                <div className="min-w-0">
                  <h3 className="font-semibold">{copy.title}</h3>
                  <p
                    className="mt-0.5 flex items-center gap-1.5 text-xs text-foreground-secondary"
                    data-tracking-summary
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "size-1.5 shrink-0 rounded-full",
                        busy ? "bg-brand-blue" : "bg-foreground-secondary",
                      )}
                    />
                    {summary ?? copy.waitingForData}
                  </p>
                </div>
              </div>
              <span className="sr-only" id={`${id}-move-hint`}>
                {copy.moveHint}
              </span>
              <Button
                aria-label={copy.resetPosition}
                className="size-8 shrink-0 text-foreground-secondary hover:text-foreground"
                size="icon"
                title={copy.resetPosition}
                type="button"
                variant="ghost"
                onClick={position.resetPosition}
              >
                <LocateFixed aria-hidden="true" className="size-4" />
              </Button>
              <Button
                aria-label={copy.close}
                className="size-8 shrink-0 text-foreground-secondary hover:text-foreground"
                size="icon"
                title={copy.close}
                type="button"
                variant="ghost"
                onClick={close}
              >
                <X aria-hidden="true" className="size-4" />
              </Button>
            </div>
            <div className="shrink-0 border-b border-border px-4">
              <div aria-label={copy.tabs} className="flex gap-5" role="tablist">
                {TABS.map(({ value, label, icon: Icon }) => (
                  <Button
                    aria-controls={id + "-" + value + "-panel"}
                    aria-label={label}
                    aria-selected={tab === value}
                    className={cn(
                      "h-11 gap-1.5 rounded-none border-b-2 border-transparent px-0 text-sm text-foreground-secondary hover:bg-transparent hover:text-foreground focus-visible:ring-inset",
                      tab === value && "border-brand-blue text-foreground",
                    )}
                    id={id + "-" + value}
                    key={value}
                    ref={value === "inspect" ? inspectRef : value === "run" ? runRef : strategiesRef}
                    role="tab"
                    tabIndex={tab === value ? 0 : -1}
                    type="button"
                    variant="ghost"
                    onClick={() => setTab(value)}
                    onKeyDown={moveTab}
                  >
                    <Icon aria-hidden="true" className="size-3.5" />
                    {label}
                    {value === "strategies" && draftChanged && (
                      <span
                        aria-hidden="true"
                        className="size-1.5 rounded-full bg-brand-blue"
                        title={copy.unsavedChanges}
                      />
                    )}
                  </Button>
                ))}
              </div>
            </div>
            {TABS.map(({ value }) => (
              <div
                aria-labelledby={id + "-" + value}
                className={cn(
                  "min-h-0 flex-1 overscroll-contain",
                  value === "inspect" ? "overflow-hidden" : "overflow-y-auto p-4",
                  tab !== value && "hidden",
                )}
                data-tracking-inspector-body={tab === value ? "" : undefined}
                hidden={tab !== value}
                id={id + "-" + value + "-panel"}
                key={value}
                role="tabpanel"
                tabIndex={0}
              >
                {value === "inspect" ? (
                  <InspectPanel bridge={bridge} search={trackSearch} onSearchChange={setTrackSearch} />
                ) : value === "run" ? (
                  <RunPanel
                    onInspectProblems={() => {
                      setTrackSearch("");
                      setFilter("problems");
                      setTab("inspect");
                    }}
                  />
                ) : (
                  <StrategiesPanel />
                )}
              </div>
            ))}
            {tab === "strategies" && <StrategyActions />}
          </section>,
          portalHost,
        )}
    </div>
  );
}
